import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay';
import { graphql } from 'relay-runtime';
import type {
  GlobalContextProviderQuery as GlobalContextProviderQueryType,
  GlobalContextProviderQuery$data as GlobalContextProviderQueryResponse,
} from './__generated__/GlobalContextProviderQuery.graphql';
import { useLoaderData } from 'react-router-dom';
import { LoaderData, PageAccess } from './Types';
import { GlobalContextProviderUpdateEntityThemeMutation$data } from './__generated__/GlobalContextProviderUpdateEntityThemeMutation.graphql';
import { ThemeProvider } from '@mui/material';
import { darkTheme } from '../portals/DarkTheme';
import { lightTheme } from '../portals/LightTheme';
import { getSystemTheme } from './Tools';
import classNames from 'classnames';
import layoutStyles from '../../../stylesheets/components/layout.module.scss';
import pluralize from 'pluralize';
import { usePortalAlert } from '../tenant_portal/PortalAlertContext';

export const GlobalContextProviderQuery = graphql`
  query GlobalContextProviderQuery {
    entities(returnSelf: true) {
      edges {
        node {
          __id
          __typename
          login {
            email
          }
          theme
        }
      }
    }
    license {
      id
      timeZone
      termOverrides {
        term
        override
      }
      marketingAccess {
        noAccess
        restrictedAccess
      }
      checkScanningAccess {
        noAccess
        restrictedAccess
      }
    }
  }
`;

export const GlobalContextProviderUpdateEntityThemeMutation = graphql`
  mutation GlobalContextProviderUpdateEntityThemeMutation(
    $input: EntityUpdateThemeInput!
  ) {
    entityUpdateTheme(input: $input) {
      errors
      entity {
        __typename
        theme
      }
    }
  }
`;

interface GlobalContext {
  theme: string | null;
  licenseId?: string | undefined;
  loginEmail?: string | undefined;
  timeZone?: string | undefined;
  toggleTheme?: () => void;
  terms?: string[];
  marketingAccess?: PageAccess;
  checkScanningAccess?: PageAccess;
  getTermOverride: (
    term: string,
    capitalize?: boolean,
    pluralize?: boolean
  ) => string;
}

export const GlobalContext = createContext<GlobalContext>({
  theme: null,
  licenseId: undefined,
  loginEmail: undefined,
  timeZone: undefined,
  toggleTheme: () => undefined,
  terms: [],
  marketingAccess: undefined,
  checkScanningAccess: undefined,
  getTermOverride: () => '',
});

interface GlobalContextProviderProps {
  children: React.ReactNode;
  isLoggedIn: boolean;
  preloadedQuery?: PreloadedQuery<GlobalContextProviderQueryType>;
  syncHtmlThemeDark1?: boolean;
}

// Login/portal dark mode uses `.dark` on a React wrapper; management site uses
// `theme-dark1` on `<html>`. CSS variables in globals.module.scss only flip under
// `:root.theme-dark1`, so sync that class when the portal is in dark mode.
const isPortalDarkTheme = (theme: string | null) =>
  theme === 'dark' || theme === 'dark1';

const useHtmlThemeDark1Sync = (theme: string | null, enabled = false) => {
  useEffect(() => {
    if (!enabled) return;

    const isDark = isPortalDarkTheme(theme);
    document.documentElement.classList.toggle('theme-dark1', isDark);

    return () => {
      document.documentElement.classList.remove('theme-dark1');
    };
  }, [enabled, theme]);
};

const LoggedOutGlobalContextProvider = ({
  children,
  syncHtmlThemeDark1 = false,
}: {
  children: React.ReactNode;
  syncHtmlThemeDark1?: boolean;
}) => {
  const [theme, setTheme] = useState(getSystemTheme);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setTheme(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useHtmlThemeDark1Sync(theme, syncHtmlThemeDark1);

  const getTermOverride = (
    term: string,
    capitalizeTerm?: boolean,
    pluralizeTerm?: boolean
  ) => {
    let override = term;
    if (capitalizeTerm) {
      override = override
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    if (pluralizeTerm) {
      override = pluralize(override);
    }
    return override;
  };

  const isDark = isPortalDarkTheme(theme);

  return (
    <div
      className={classNames(layoutStyles.fullHeight, layoutStyles.fullWidth, {
        dark: isDark,
      })}
    >
      <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
        <GlobalContext.Provider
          value={{
            theme,
            licenseId: undefined,
            loginEmail: undefined,
            timeZone: undefined,
            getTermOverride,
          }}
        >
          {children}
        </GlobalContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export const GlobalContextProvider = ({
  children,
  isLoggedIn,
  preloadedQuery,
  syncHtmlThemeDark1,
}: GlobalContextProviderProps) => {
  if (isLoggedIn)
    if (preloadedQuery) {
      return (
        <RelayThemeProviderLoadQueryWrapper preloadedQuery={preloadedQuery}>
          {children}
        </RelayThemeProviderLoadQueryWrapper>
      );
    } else {
      return (
        <RelayThemeProviderRouterWrapper>
          {children}
        </RelayThemeProviderRouterWrapper>
      );
    }
  else {
    return (
      <LoggedOutGlobalContextProvider syncHtmlThemeDark1={syncHtmlThemeDark1}>
        {children}
      </LoggedOutGlobalContextProvider>
    );
  }
};

const RelayThemeProviderRouterWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const loaderQueryReference = (useLoaderData() as LoaderData)
    .GlobalContextProviderLoadQuery;
  const [queryReference] = useQueryLoader<GlobalContextProviderQueryType>(
    GlobalContextProviderQuery,
    loaderQueryReference
  );

  const data = usePreloadedQuery(
    GlobalContextProviderQuery,
    queryReference as PreloadedQuery<
      GlobalContextProviderQueryType,
      GlobalContextProviderQueryResponse
    >
  );

  return <RelayThemeProvider data={data}>{children}</RelayThemeProvider>;
};

const RelayThemeProviderLoadQueryWrapper = ({
  children,
  preloadedQuery,
}: {
  children: React.ReactNode;
  preloadedQuery: PreloadedQuery<GlobalContextProviderQueryType>;
}) => {
  const [queryReference] = useQueryLoader<GlobalContextProviderQueryType>(
    GlobalContextProviderQuery,
    preloadedQuery
  );

  const data = usePreloadedQuery(
    GlobalContextProviderQuery,
    queryReference as PreloadedQuery<
      GlobalContextProviderQueryType,
      GlobalContextProviderQueryResponse
    >
  );

  return <RelayThemeProvider data={data}>{children}</RelayThemeProvider>;
};

const RelayThemeProvider = ({
  children,
  data,
}: {
  children: React.ReactNode;
  data: GlobalContextProviderQueryResponse;
}) => {
  const showAlert = usePortalAlert();
  const [theme, setTheme] = useState(
    data?.entities?.edges?.[0]?.node?.theme || null
  );
  const licenseId = data?.license?.id;
  const loginEmail = data?.entities?.edges?.[0]?.node?.login?.email;
  const timeZone = data?.license?.timeZone;
  const marketingAccess = data?.license?.marketingAccess;
  const checkScanningAccess = data?.license?.checkScanningAccess;

  const entityId = data?.entities?.edges?.[0]?.node?.__id || null;
  const entityTypename = data?.entities?.edges?.[0]?.node?.__typename || null;

  const [commitMutation] = useMutation(
    GlobalContextProviderUpdateEntityThemeMutation
  );

  const updateTheme = useCallback(
    (theme: string) => {
      commitMutation({
        variables: {
          input: {
            entityThemeData: {
              id: entityId,
              theme: theme,
            },
          },
        },
        onCompleted: (response) => {
          const responseData =
            response as GlobalContextProviderUpdateEntityThemeMutation$data;
          if (!responseData || !responseData.entityUpdateTheme) {
            showAlert('There was an error updating the theme');
            return;
          }
          if (
            responseData.entityUpdateTheme.errors &&
            responseData.entityUpdateTheme.errors.length > 0
          ) {
            showAlert(responseData.entityUpdateTheme.errors.join('<br>'));
            return;
          }
        },
        onError: (error) => {
          showAlert('There was an error updating the theme');
          console.error(error);
        },
        optimisticResponse: {
          entityUpdateTheme: {
            entity: {
              id: entityId,
              __typename: entityTypename,
              theme: theme === 'standard' ? 'dark' : 'standard',
            },
            errors: null,
          },
        },
      });
    },
    [commitMutation, entityId, entityTypename, showAlert]
  );

  useEffect(() => {
    if (theme && theme !== data?.entities?.edges?.[0]?.node?.theme) {
      updateTheme(theme);
    }
  }, [theme, data?.entities?.edges, updateTheme]);

  const toggleTheme = () => {
    setTheme(theme === 'standard' ? 'dark' : 'standard');
  };

  if (!theme) setTheme(getSystemTheme());

  const terms = data?.license?.termOverrides.map(
    (termOverride) => termOverride.term
  );
  const termOverrideHash = (data?.license?.termOverrides || []).reduce(
    (acc: Record<string, string>, termOverride) => {
      acc[termOverride.term] = termOverride.override;
      return acc;
    },
    {}
  );
  const getTermOverride = useCallback(
    (term: string, capitalizeTerm?: boolean, pluralizeTerm?: boolean) => {
      let override = termOverrideHash[term] || '';
      if (capitalizeTerm) {
        override = override
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      if (pluralizeTerm) {
        override = pluralize(override);
      }
      return override;
    },
    [termOverrideHash]
  );

  return (
    // <div className={theme === 'dark' ? 'dark' : undefined}>
    <div
      className={classNames(layoutStyles.fullHeight, layoutStyles.fullWidth, {
        dark: theme === 'dark',
      })}
    >
      <ThemeProvider theme={theme === 'dark' ? darkTheme : lightTheme}>
        <GlobalContext.Provider
          value={{
            theme,
            licenseId,
            loginEmail,
            timeZone,
            toggleTheme,
            terms,
            marketingAccess,
            checkScanningAccess,
            getTermOverride,
          }}
        >
          {children}
        </GlobalContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export const useGlobalContext = () => useContext(GlobalContext);
